import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AllInclusive,
  Campaign,
  HelpOutline,
  Hub,
  Refresh,
  Schedule,
  SmartToy,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import { useLanguage } from '../../hooks/useLanguage';
import { useListSearchParams } from '../../hooks/useListSearchParams';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import useSystemDashboardCockpit from '../../hooks/useSystemDashboardCockpit';
import type { SystemDashboardCockpitRuntimeProductSummary } from '../../types';
import { formatDateOnly, formatDateTime } from '../../utils/date';

const DISPLAY_FONT = '"Barlow Condensed", "Oswald", sans-serif';
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';

type SystemCockpitDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';
type SystemCockpitDateRangeOption = {
  value: SystemCockpitDateRangePreset;
  label: string;
};

function isSystemCockpitDateRangePreset(value: string | null | undefined): value is SystemCockpitDateRangePreset {
  return value === 'today' || value === 'yesterday' || value === 'last_7_days' || value === 'period';
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      gap={1.5}
      sx={{ mb: 2.5 }}
    >
      <Box>
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            letterSpacing: '0.18em',
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: DISPLAY_FONT,
            lineHeight: 0.95,
            letterSpacing: '-0.04em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.8, maxWidth: 760 }}>
          {subtitle}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}

function MetricTitle({
  label,
  hint,
}: {
  label: string;
  hint: string;
}) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center">
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </Typography>
      <Tooltip title={hint} arrow>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Stack>
  );
}

function MetricCard({
  icon,
  label,
  hint,
  value,
  subtitle,
  accent,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
  value: string;
  subtitle?: string;
  accent: string;
}) {
  return (
    <Paper
      sx={{
        p: 2.2,
        borderRadius: '18px',
        border: `1px solid ${alpha(accent, 0.16)}`,
        backgroundColor: alpha(accent, 0.04),
      }}
    >
      <Stack spacing={1.4}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <MetricTitle label={label} hint={hint} />
          <Box
            sx={{
              width: 42,
              height: 42,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '14px',
              color: accent,
              bgcolor: alpha(accent, 0.1),
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Typography
          variant="h3"
          sx={{
            fontFamily: DISPLAY_FONT,
            lineHeight: 0.95,
            letterSpacing: '-0.04em',
          }}
        >
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ProductRuntimeCard({
  productLabel,
  summary,
  accent,
  formatNumber,
  formatPercent,
}: {
  productLabel: string;
  summary: SystemDashboardCockpitRuntimeProductSummary;
  accent: string;
  formatNumber: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
}) {
  const { t } = useTranslation(['pages']);
  const contractedLabel = summary.hasUnlimitedCapacity
    ? t('pages:dashboard.budget.unlimited')
    : formatNumber(summary.contractedConcurrencyTotal);

  const runtimeRows = [
    {
      label: t('pages:dashboard.system.runtime.products.tenants'),
      value: formatNumber(summary.activeTenantsCount),
    },
    {
      label: t('pages:dashboard.system.runtime.products.subscriptions'),
      value: formatNumber(summary.activeSubscriptionsCount),
    },
    {
      label: t('pages:dashboard.system.runtime.products.contracted'),
      value: contractedLabel,
    },
    {
      label: t('pages:dashboard.system.runtime.products.occupied'),
      value: formatNumber(summary.occupiedConcurrencyTotal),
    },
    {
      label: t('pages:dashboard.system.runtime.products.occupancyRate'),
      value: summary.occupancyRate != null ? formatPercent(summary.occupancyRate) : t('pages:dashboard.notAvailable'),
    },
  ];

  return (
    <Paper
      sx={{
        p: 2.2,
        borderRadius: '18px',
        border: `1px solid ${alpha(accent, 0.16)}`,
        backgroundColor: alpha(accent, 0.04),
      }}
    >
      <Stack spacing={1.8}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: DISPLAY_FONT,
            letterSpacing: '-0.03em',
            textTransform: 'uppercase',
          }}
        >
          {productLabel}
        </Typography>
        <Stack spacing={1.1}>
          {runtimeRows.map((row) => (
            <Stack key={row.label} direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function SystemCockpitTab() {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const { language } = useLanguage();
  const { locale, formatCurrency, formatDecimal, formatNumber, formatPercent } = useNumberFormat();
  const { filters, setParams } = useListSearchParams({
    filters: ['date_range', 'start_date', 'end_date'],
  });

  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);

  const dateRangeOptions = useMemo<SystemCockpitDateRangeOption[]>(
    () => [
      { value: 'today', label: t('pages:calls.filters.today') },
      { value: 'yesterday', label: t('pages:calls.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:calls.filters.last7Days') },
      { value: 'period', label: t('pages:calls.filters.period') },
    ],
    [t],
  );

  const startDateFilterValue = filters.start_date ?? '';
  const endDateFilterValue = filters.end_date ?? '';
  const selectedDateRange = useMemo<SystemCockpitDateRangePreset | null>(() => {
    if (isSystemCockpitDateRangePreset(filters.date_range)) {
      return filters.date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.date_range, startDateFilterValue]);

  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );

  const startDateValue = useMemo<Dayjs | null>(() => {
    if (!startDateFilterValue) {
      return null;
    }
    const value = dayjs(startDateFilterValue);
    return value.isValid() ? value : null;
  }, [startDateFilterValue]);

  const endDateValue = useMemo<Dayjs | null>(() => {
    if (!endDateFilterValue) {
      return null;
    }
    const value = dayjs(endDateFilterValue);
    return value.isValid() ? value : null;
  }, [endDateFilterValue]);

  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || null,
        endDate: endDateFilterValue || null,
      };
    }
    if (!selectedDateRange) {
      return {
        startDate: null,
        endDate: null,
      };
    }

    const today = dayjs();
    if (selectedDateRange === 'today') {
      const value = today.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    if (selectedDateRange === 'yesterday') {
      const value = today.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    return {
      startDate: today.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT),
      endDate: today.format(DATE_FILTER_PARAM_FORMAT),
    };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);

  const { cockpit, isLoading, isRefreshing, error, refresh } = useSystemDashboardCockpit({
    dateFrom: resolvedDateParams.startDate,
    dateTo: resolvedDateParams.endDate,
  });

  const runtime = cockpit?.runtime ?? null;
  const history = cockpit?.history ?? null;
  const capacityLoadValue = runtime
    ? runtime.hasUnlimitedCapacity
      ? `${formatNumber(runtime.occupiedConcurrencyTotal)} / ∞`
      : `${formatNumber(runtime.occupiedConcurrencyTotal)} / ${formatNumber(runtime.contractedConcurrencyTotal)}`
    : t('pages:dashboard.notAvailable');
  const campaignsSubtitle = runtime
    ? t('pages:dashboard.system.runtime.activeCampaignsSplit', {
      ai: formatNumber(runtime.activeCampaignsByType.ai),
      reverseIvr: formatNumber(runtime.activeCampaignsByType.reverseIvr),
    })
    : undefined;
  const activeSubscriptionsTotal = runtime
    ? runtime.products.ai.activeSubscriptionsCount + runtime.products.reverseIvr.activeSubscriptionsCount
    : 0;
  const historyPeriodLabel = history
    ? `${formatDateOnly(`${history.dateFrom}T12:00:00`, { locale, fallback: history.dateFrom })} → ${formatDateOnly(`${history.dateTo}T12:00:00`, { locale, fallback: history.dateTo })}`
    : null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gap: 3,
        px: { xs: 0, lg: 0.5 },
      }}
    >
      {(isLoading || isRefreshing) ? (
        <LinearProgress sx={{ position: 'relative', zIndex: 1, borderRadius: 999 }} />
      ) : null}

      <Paper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          pt: { xs: 8.5, md: 3.5 },
          borderRadius: '20px',
          border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
          background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 56%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {cockpit ? (
          <Chip
            icon={<Schedule sx={{ fontSize: 18 }} />}
            label={t('pages:dashboard.system.snapshotLabel', {
              value: formatDateTime(cockpit.snapshotAt, { locale, fallback: cockpit.snapshotAt }),
            })}
            sx={{
              position: 'absolute',
              top: { xs: 18, md: 24 },
              right: { xs: 18, md: 24 },
              maxWidth: { xs: 'calc(100% - 36px)', md: 'none' },
              height: 40,
              px: 1.2,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.background.paper, 0.82),
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          />
        ) : null}

        <SectionHeader
          eyebrow={t('pages:dashboard.system.eyebrow')}
          title={t('pages:dashboard.system.title')}
          subtitle={t('pages:dashboard.system.subtitle')}
          action={(
            <Button variant="outlined" startIcon={<Refresh />} onClick={refresh}>
              {t('common:actions.refresh')}
            </Button>
          )}
        />

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.2} flexWrap="wrap" sx={{ mt: 2 }}>
          <Autocomplete<SystemCockpitDateRangeOption, false, false, false>
            options={dateRangeOptions}
            value={dateRangeOption}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            onChange={(_, selectedOption) => {
              const nextDateRange = selectedOption?.value ?? null;
              if (!nextDateRange) {
                setParams({ date_range: null, start_date: null, end_date: null });
                return;
              }
              if (nextDateRange === 'period') {
                setParams({ date_range: nextDateRange });
                return;
              }
              setParams({ date_range: nextDateRange, start_date: null, end_date: null });
            }}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', md: '0 0 240px' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('pages:calls.filters.dateRangeLabel')}
              />
            )}
          />
          {selectedDateRange === 'period' ? (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
              <>
                <DatePicker
                  label={t('pages:calls.filters.fromDate')}
                  value={startDateValue}
                  onChange={(value) => {
                    const formattedDate = value && value.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null;
                    setParams({ date_range: 'period', start_date: formattedDate });
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180, flex: { xs: '1 1 100%', md: '0 0 180px' } },
                    },
                  }}
                />
                <DatePicker
                  label={t('pages:calls.filters.toDate')}
                  value={endDateValue}
                  onChange={(value) => {
                    const formattedDate = value && value.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null;
                    setParams({ date_range: 'period', end_date: formattedDate });
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180, flex: { xs: '1 1 100%', md: '0 0 180px' } },
                    },
                  }}
                />
              </>
            </LocalizationProvider>
          ) : null}
          {historyPeriodLabel ? (
            <Chip
              label={t('pages:dashboard.system.history.periodChip', { value: historyPeriodLabel })}
              sx={{ height: 40, borderRadius: 999, ml: { md: 'auto' } }}
            />
          ) : null}
        </Stack>
      </Paper>

      {error ? (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={refresh}>
              {t('pages:dashboard.retryLoad')}
            </Button>
          )}
        >
          {t('pages:dashboard.system.loadFailed')}
        </Alert>
      ) : null}

      <Paper sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '20px' }}>
        <SectionHeader
          eyebrow={t('pages:dashboard.system.runtime.eyebrow')}
          title={t('pages:dashboard.system.runtime.title')}
          subtitle={t('pages:dashboard.system.runtime.subtitle')}
        />

        {isLoading && !runtime ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={148} />
            ))}
          </Box>
        ) : runtime ? (
          <Stack spacing={2.4}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <MetricCard
                icon={<Hub />}
                label={t('pages:dashboard.system.runtime.cards.contractedCapacity')}
                hint={t('pages:dashboard.system.runtime.hints.contractedCapacity')}
                value={runtime.hasUnlimitedCapacity ? t('pages:dashboard.budget.unlimited') : formatNumber(runtime.contractedConcurrencyTotal)}
                subtitle={t('pages:dashboard.system.runtime.cards.contractedCapacitySubtitle', {
                  occupied: formatNumber(runtime.occupiedConcurrencyTotal),
                })}
                accent={theme.palette.info.main}
              />
              <MetricCard
                icon={<AllInclusive />}
                label={t('pages:dashboard.system.runtime.cards.capacityLoad')}
                hint={t('pages:dashboard.system.runtime.hints.capacityLoad')}
                value={capacityLoadValue}
                subtitle={t('pages:dashboard.system.runtime.cards.capacityLoadSubtitle', {
                  activeSubscriptions: formatNumber(activeSubscriptionsTotal),
                })}
                accent={theme.palette.primary.main}
              />
              <MetricCard
                icon={<Campaign />}
                label={t('pages:dashboard.system.runtime.cards.activeCampaigns')}
                hint={t('pages:dashboard.system.runtime.hints.activeCampaigns')}
                value={formatNumber(runtime.activeCampaignsTotal)}
                subtitle={campaignsSubtitle}
                accent={theme.palette.success.main}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <ProductRuntimeCard
                productLabel={t('pages:dashboard.products.ai')}
                summary={runtime.products.ai}
                accent={theme.palette.primary.main}
                formatNumber={formatNumber}
                formatPercent={formatPercent}
              />
              <ProductRuntimeCard
                productLabel={t('pages:dashboard.products.reverseIvr')}
                summary={runtime.products.reverseIvr}
                accent={theme.palette.warning.main}
                formatNumber={formatNumber}
                formatPercent={formatPercent}
              />
              <MetricCard
                icon={<SmartToy />}
                label={t('pages:dashboard.system.runtime.cards.occupancyRate')}
                hint={t('pages:dashboard.system.runtime.hints.occupancyRate')}
                value={runtime.occupancyRate != null ? formatPercent(runtime.occupancyRate) : t('pages:dashboard.notAvailable')}
                subtitle={t('pages:dashboard.system.runtime.cards.occupancyRateSubtitle')}
                accent={theme.palette.secondary.main}
              />
            </Box>
          </Stack>
        ) : null}
      </Paper>

      <Paper sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '20px' }}>
        <SectionHeader
          eyebrow={t('pages:dashboard.system.history.eyebrow')}
          title={t('pages:dashboard.system.history.title')}
          subtitle={t('pages:dashboard.system.history.subtitle')}
        />

        {isLoading && !history ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={148} />
            ))}
          </Box>
        ) : history ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            <MetricCard
              icon={<SmartToy />}
              label={t('pages:dashboard.system.history.cards.aiMinutes')}
              hint={t('pages:dashboard.system.history.hints.aiMinutes')}
              value={`${formatDecimal(history.aiMinutesTotal, 2)} min`}
              subtitle={t('pages:dashboard.system.history.cards.aiMinutesSubtitle', {
                seconds: formatNumber(history.aiBilledSecondsTotal),
              })}
              accent={theme.palette.primary.main}
            />
            <MetricCard
              icon={<Schedule />}
              label={t('pages:dashboard.system.history.cards.aiAverageCostPerMinute')}
              hint={t('pages:dashboard.system.history.hints.aiAverageCostPerMinute')}
              value={formatCurrency(history.aiAverageCostPerMinuteUsd, 'USD', 4)}
              subtitle={t('pages:dashboard.system.history.cards.aiAverageCostPerMinuteSubtitle', {
                total: formatCurrency(history.aiEstimatedCostUsdTotal, 'USD', 4),
              })}
              accent={theme.palette.success.main}
            />
            <MetricCard
              icon={<Hub />}
              label={t('pages:dashboard.system.history.cards.cdrBillableMinutes')}
              hint={t('pages:dashboard.system.history.hints.cdrBillableMinutes')}
              value={`${formatDecimal(history.cdrBillableMinutesTotal, 2)} min`}
              subtitle={t('pages:dashboard.system.history.cards.cdrBillableMinutesSubtitle', {
                seconds: formatNumber(history.cdrBillableSecondsTotal),
              })}
              accent={theme.palette.warning.main}
            />
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
